import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaTimes } from 'react-icons/fa';
import { Wallet } from 'lucide-react';
import EligibleCashbackIcon from '../images/EligibleCashback.svg';
import RedeemableCashbackIcon from '../images/RedeemableCashback.svg';
import LifetimeCashbackIcon from '../images/LifetimeCashback.svg';
import {
  getSubscriberProfile,
  getWalletSnapshot,
  getDiagnosticBookingSnapshot,
  getDiagnosticBookingDetail,
  updateDiagnosticBookingStatus,
  updateDiagnosticBookingReport,
  sendDiagnosticBookingReport
} from '../services/subscriberWalletService';
import { DIAGNOSTIC_LIST_PATH, DIAGNOSTIC_LIST_URL_KEY, getStatusBadgeStyle, resolveBookingBackTarget, resolveListBackTarget } from '../utils/subscriberHelpers';
import DiagnosticBookingDetailModal from '../components/diagnostic/DiagnosticBookingDetailModal';

// Feature flag — the backend diagnostic update-status endpoint is NOT live yet.
// Keep the manual status-change button hidden until the endpoint ships and its
// status vocabulary is confirmed. Flip to true (with human approval) at that point.
const DIAGNOSTIC_STATUS_UPDATE_ENABLED = true;

// The snapshot/list API returns humanized status LABELS ("Phlebo On The Way"); normalize
// back to the backend token ("phlebo_on_the_way") for flow logic + the update-status call.
const toStatusToken = (status) => String(status || '').toLowerCase().trim().replace(/\s+/g, '_');

// Strictly-sequential forward flow for AyushPay-owned (non-partner) lab bookings.
// confirmed → sample_collected → report_generated → completed.
const DIAG_SEQUENTIAL_NEXT = {
  confirmed: 'sample_collected',
  sample_collected: 'report_generated',
  report_generated: 'completed',
  completed: null,
};
// States from which Ops may act (advance and/or refund).
const DIAG_ACTIVE_STATES = ['confirmed', 'sample_collected', 'report_generated', 'completed'];

// Targets Ops may move a booking to: the sequential next (if any) PLUS 'refunded'
// (always available while active). Mirrors backend status_flow.allowed_targets.
const allowedDiagTargets = (currentStatus) => {
  const cur = toStatusToken(currentStatus);
  const out = [];
  const nxt = DIAG_SEQUENTIAL_NEXT[cur];
  if (nxt) out.push(nxt);
  if (DIAG_ACTIVE_STATES.includes(cur)) out.push('refunded');
  return out;
};

// Display-label overrides for specific tokens (must match backend _format_status).
const STATUS_LABELS = { completed: 'Complete' };

// Humanize a snake_case status for display, e.g. "sample_collected" → "Sample Collected".
const humanizeStatus = (status) => {
  const token = toStatusToken(status);
  if (STATUS_LABELS[token]) return STATUS_LABELS[token];
  return String(status || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Partner (third-party) providers fulfil status automatically — no manual button.
// AyushPay's own labs (any non-partner provider) get the manual status-change action.
const PARTNER_PROVIDERS = ['healthians'];
// contract: getDiagnosticBookingSnapshot serializer — item.service_provider, item.third_party_booking_ref_code
const isPartnerBooking = (txn) => {
  const provider = String(txn?.service_provider || '').toLowerCase().trim();
  const matchesPartner = PARTNER_PROVIDERS.some((p) => provider.includes(p));
  return matchesPartner || Boolean(txn?.third_party_booking_ref_code);
};

// Currency formatter with truncation to 2 decimals (no rounding)
const currencyRounded = (value) => {
  const num = Number(value);
  if (isNaN(num)) return '₹0';
  const truncated = Math.trunc(num * 100) / 100;
  const fixed = truncated.toFixed(2);
  const [wholePart, decimalPart] = fixed.split('.');
  const formattedWhole = Number(wholePart).toLocaleString('en-IN');
  if (decimalPart === '00') {
    return `₹${formattedWhole}`;
  }
  return `₹${formattedWhole}.${decimalPart}`;
};

// Format date to DD-MMM-YYYY (e.g., 22-Nov-2025)
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  // Handle case where dateString is an object with start_date and end_date
  if (typeof dateString === 'object' && dateString !== null && !(dateString instanceof Date)) {
    if (dateString.start_date && dateString.end_date) {
      // Format as date range - handle nested objects recursively
      let start = dateString.start_date;
      let end = dateString.end_date;
      // If start_date or end_date are objects, format them recursively
      if (typeof start === 'object' && start !== null && !(start instanceof Date)) {
        start = formatDate(start);
      } else {
        start = formatDate(String(start));
      }

      if (typeof end === 'object' && end !== null && !(end instanceof Date)) {
        end = formatDate(end);
      } else {
        end = formatDate(String(end));
      }

      return `${start} - ${end}`;
    } else {
      return 'N/A';
    }
  }

  // Ensure we're working with a string
  if (typeof dateString !== 'string') {
    return 'N/A';
  }

  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let date;

    // If it's a date range (e.g., "2025-05-16 to 2026-05-16" or "16/05/2025 - 16/05/2026")
    if (dateString.includes(' to ') || dateString.includes(' - ')) {
      const separator = dateString.includes(' to ') ? ' to ' : ' - ';
      const [startDate, endDate] = dateString.split(separator);
      const formattedStart = formatDate(startDate.trim());
      const formattedEnd = formatDate(endDate.trim());
      return `${formattedStart} - ${formattedEnd}`;
    }

    // Handle DD/MM/YYYY or DD/MM/YY format
    if (dateString.includes('/') && dateString.split('/').length === 3) {
      const parts = dateString.split('/');
      const day = parts[0].padStart(2, '0');
      const monthNum = parts[1].padStart(2, '0');
      let year = parts[2];

      // If year is 2 digits, convert to 4 digits
      if (year.length === 2) {
        const yearNum = parseInt(year, 10);
        year = yearNum > 50 ? `19${year}` : `20${year}`;
      }

      // Create date object to get month name
      date = new Date(`${year}-${monthNum}-${day}`);
      if (!isNaN(date.getTime())) {
        const month = monthNames[date.getMonth()];
        return `${day}-${month}-${year}`;
      }
    }

    // Try to parse the date
    date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch {
    return dateString;
  }
};

// Format date and time as DD-MMM-YYYY HH:MM AM/PM (12-hour format)
// Handles API format: "DD-MM-YYYY HH:MM" and converts to "DD-MMM-YYYY HH:MM AM/PM"
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';

  // Handle case where dateString is an object with start_date and end_date
  if (typeof dateString === 'object' && dateString !== null && !(dateString instanceof Date)) {
    // Prevent infinite recursion - if start_date or end_date are also objects, format them recursively
    if (dateString.start_date && dateString.end_date) {
      // Format as date range - handle nested objects recursively
      let start = dateString.start_date;
      let end = dateString.end_date;

      // If start_date or end_date are objects, format them recursively
      if (typeof start === 'object' && start !== null && !(start instanceof Date)) {
        start = formatDateTime(start);
      } else {
        start = formatDateTime(String(start));
      }

      if (typeof end === 'object' && end !== null && !(end instanceof Date)) {
        end = formatDateTime(end);
      } else {
        end = formatDateTime(String(end));
      }

      return `${start} to ${end}`;
    } else {
      // If it's an object but doesn't have the expected structure, return 'N/A' to avoid rendering the object
      return 'N/A';
    }
  }

  // Ensure we're working with a string
  if (typeof dateString !== 'string') {
    return 'N/A';
  }

  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Check if the date is already in DD-MM-YYYY HH:MM format (from API)
    if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}$/.test(dateString)) {
      // Parse DD-MM-YYYY HH:MM format
      const [datePart, timePart] = dateString.split(' ');
      const [day, monthNum, year] = datePart.split('-');
      const [hours24, minutes] = timePart.split(':');

      // Convert month number to month abbreviation
      const month = monthNames[parseInt(monthNum, 10) - 1] || monthNum;

      // Convert to 12-hour format
      let hours = parseInt(hours24, 10);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12
      const formattedHours = String(hours).padStart(2, '0');

      return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
    }

    // Fallback: try to parse as standard date string
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    // Convert to 12-hour format
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const formattedHours = String(hours).padStart(2, '0');

    return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
  } catch {
    return 'N/A';
  }
};

const DiagnosticBookingProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');

  // Where the secondary back button goes: the subscriber this booking was opened from when
  // the user came through Transaction History, otherwise the Diagnostics list page/filters it was opened from.
  const backTarget = resolveBookingBackTarget(searchParams, {
    label: 'Back to Diagnostics',
    path: resolveListBackTarget(location.state, DIAGNOSTIC_LIST_PATH, DIAGNOSTIC_LIST_URL_KEY),
  });

  // State for API data
  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [diagnosticSnapshot, setDiagnosticSnapshot] = useState(null);
  const [diagnosticSnapshotLoading, setDiagnosticSnapshotLoading] = useState(false);
  const [diagnosticSnapshotError, setDiagnosticSnapshotError] = useState(null);

  // State for detail modal
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [bookingDetail, setBookingDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [partnerDataExpanded, setPartnerDataExpanded] = useState(false);

  // Diagnostic snapshot pagination
  const [snapshotPage, setSnapshotPage] = useState(1);
  const SNAPSHOT_PAGE_SIZE = 25;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLoadingDataRef = useRef(false);
  const isSnapshotLoadingRef = useRef(false);
  const autoOpenedDetailRef = useRef(false);

  const [showPhone, setShowPhone] = useState(false);
  const [mobilesUnmasked] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Mount state for portal
  useEffect(() => {
    setMounted(true);
  }, []);

  // Mask mobile number function
  const maskMobile = (value) => {
    if (!value) return 'N/A';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length <= 4) return `******${digits}`;
    const last4 = digits.slice(-4);
    return `******${last4}`;
  };

  // Auto-detect role from authentication
  const [currentRole] = useState(() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'SuperAdmin',
      'wallet_finance': 'Finance',
      'wallet_ops': 'Ops',
      'wallet_support': 'Support'
    };
    if (roleMapping[authRole]) {
      return roleMapping[authRole];
    }
    return null;
  });

  // Role-based permissions
  const isFinance = currentRole === 'Finance';
  const isSupport = currentRole === 'Support';
  const isOpsOrSuperAdmin = currentRole === 'Ops' || currentRole === 'SuperAdmin';

  // State for manual status-update (AyushPay-owned labs only)
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false);
  const [selectedTransactionForStatus, setSelectedTransactionForStatus] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState(null);

  // Mask phone for display
  const maskedPhone = React.useMemo(() => {
    if (!profile) return 'N/A';
    const phone = profile?.personal?.mobile || profile?.phoneFull || '';
    if (!phone || phone.trim() === '') return 'N/A';
    return maskMobile(phone);
  }, [profile]);

  // Load profile and wallet data
  useEffect(() => {
    if (isLoadingDataRef.current || !id || !currentRole) {
      return;
    }

    isLoadingDataRef.current = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [profileData, walletData] = await Promise.allSettled([
          getSubscriberProfile(id, currentRole).catch(err => {
            return { error: 'Profile API failed', details: err.message };
          }),
          getWalletSnapshot(id, currentRole).catch(err => {
            return { error: 'Wallet API failed', details: err.message };
          })
        ]);

        // Handle profile data
        if (profileData.status === 'fulfilled' && profileData.value) {
          const response = profileData.value;
          let profileToSet = null;

          if (response.msg && typeof response.msg === 'object') {
            profileToSet = response.msg;
          } else if (response.data && typeof response.data === 'object') {
            profileToSet = response.data;
          } else if (typeof response === 'object') {
            profileToSet = response;
          }

          if (profileToSet) {
            setProfile(profileToSet);
          }
        }

        // Handle wallet data
        if (walletData.status === 'fulfilled' && walletData.value) {
          const response = walletData.value;
          let walletToSet = null;

          if (response.wallet_snapshot) {
            walletToSet = { wallet_snapshot: response.wallet_snapshot };
          } else if (response.msg && response.msg.wallet_snapshot) {
            walletToSet = { wallet_snapshot: response.msg.wallet_snapshot };
          } else if (response.data && response.data.wallet_snapshot) {
            walletToSet = { wallet_snapshot: response.data.wallet_snapshot };
          } else if (response.error) {
            walletToSet = { error: response.error };
          }

          if (walletToSet) {
            setWallet(walletToSet);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load subscriber data');
      } finally {
        setLoading(false);
        isLoadingDataRef.current = false;
      }
    };

    loadData();
  }, [id, currentRole]);

  // Load diagnostic booking snapshot. Extracted to a useCallback so it can be
  // re-invoked after a manual status update (reset isSnapshotLoadingRef first).
  const loadDiagnosticSnapshot = useCallback(async () => {
    if (!bookingId || !currentRole) {
      return;
    }
    // Prevent duplicate calls - if already loading, skip
    if (isSnapshotLoadingRef.current) {
      return;
    }
    // Mark as loading
    isSnapshotLoadingRef.current = true;

    try {
      setDiagnosticSnapshotLoading(true);
      setDiagnosticSnapshotError(null);

      const params = {
        page: snapshotPage,
        items: SNAPSHOT_PAGE_SIZE
      };

      const response = await getDiagnosticBookingSnapshot(currentRole, bookingId, params);

      if (response && response.msg) {
        // Calculate total_pages from pagination if it exists
        if (response.msg.pagination) {
          const calculatedTotalPages = Math.ceil(response.msg.pagination.total / response.msg.pagination.items);
          setDiagnosticSnapshot({
            ...response.msg,
            pagination: {
              ...response.msg.pagination,
              total_pages: calculatedTotalPages
            }
          });
        } else {
          setDiagnosticSnapshot(response.msg);
        }
      } else if (response && response.data) {
        // Calculate total_pages from pagination if it exists
        if (response.data.pagination) {
          const calculatedTotalPages = Math.ceil(response.data.pagination.total / response.data.pagination.items);
          setDiagnosticSnapshot({
            ...response.data,
            pagination: {
              ...response.data.pagination,
              total_pages: calculatedTotalPages
            }
          });
        } else {
          setDiagnosticSnapshot(response.data);
        }
      } else {
        setDiagnosticSnapshotError('Failed to load diagnostic booking snapshot');
      }
    } catch (error) {
      console.error('Error loading diagnostic booking snapshot:', error);
      setDiagnosticSnapshotError('Failed to load diagnostic booking snapshot');
    } finally {
      setDiagnosticSnapshotLoading(false);
      isSnapshotLoadingRef.current = false;
    }
  }, [bookingId, currentRole, snapshotPage]);

  useEffect(() => {
    loadDiagnosticSnapshot();
  }, [loadDiagnosticSnapshot]);

  // Load booking detail when a row is clicked
  const handleRowClick = async (transactionId) => {
    if (!transactionId || !currentRole) return;

    setSelectedBookingId(transactionId);
    setDetailLoading(true);
    setDetailError(null);
    setBookingDetail(null);

    try {
      const response = await getDiagnosticBookingDetail(currentRole, transactionId);

      if (response && response.msg) {
        setBookingDetail(response.msg);
      } else if (response && response.data) {
        setBookingDetail(response.data);
      } else {
        setDetailError('Failed to load booking details');
      }
    } catch (error) {
      console.error('Error loading booking detail:', error);
      setDetailError('Failed to load booking details');
    } finally {
      setDetailLoading(false);
    }
  };

  // Close modal
  const closeModal = () => {
    setSelectedBookingId(null);
    setBookingDetail(null);
    setDetailError(null);
    setPartnerDataExpanded(false);
  };

  // open_detail=1: open the detail for the exact transaction named in the URL instead of
  // leaving the list showing.
  //
  // The subscriber's Transaction History no longer produces these URLs — it renders this
  // module's detail popup in place now — but links already pasted into tickets and Slack
  // still land here, and must still work. The Diagnostic Subscribers list navigates here
  // without the flag and is unaffected.
  useEffect(() => {
    if (autoOpenedDetailRef.current) return;
    if (searchParams.get('open_detail') !== '1' || !bookingId) return;
    const items = diagnosticSnapshot?.items;
    if (!Array.isArray(items) || items.length === 0) return;
    // contract: loan-and-subscription internalapi/views/user_type/shared/diagnostic_subscribers/snapshot.py:354,358-362
    const clicked = items.find((txn) => String(txn.transaction_id) === String(bookingId));
    if (!clicked) return; // not on this snapshot page - leave the list showing
    autoOpenedDetailRef.current = true;
    handleRowClick(clicked.transaction_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagnosticSnapshot, bookingId, searchParams]);

  // Handle partner data expand - toggle visibility
  const handlePartnerDataExpand = () => {
    setPartnerDataExpanded(!partnerDataExpanded);
  };

  // ── Manual status update (AyushPay-owned labs only) ──
  const closeStatusUpdateModal = () => {
    if (updatingStatus) return; // don't close mid-request
    setShowStatusUpdateModal(false);
    setSelectedTransactionForStatus(null);
    setStatusUpdateError(null);
  };

  // ---- Diagnostic report: attach a link, then send it on demand ----
  // Reports live on the booking as a list of URLs. Attaching appends; sending takes the
  // one report Ops picked, because a booking can cover several family members and the
  // list carries no per-member association.
  const [reportModalTxn, setReportModalTxn] = useState(null);
  const [reportUrlInput, setReportUrlInput] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportNotice, setReportNotice] = useState(null);

  const openReportModal = (e, txn) => {
    e.stopPropagation();
    setReportModalTxn(txn);
    setReportUrlInput('');
    setReportError(null);
    setReportNotice(null);
  };

  const closeReportModal = () => {
    setReportModalTxn(null);
    setReportUrlInput('');
    setReportError(null);
    setReportNotice(null);
  };

  const refreshAfterReportChange = async () => {
    isSnapshotLoadingRef.current = false;
    await loadDiagnosticSnapshot();
  };

  const handleAttachReport = async () => {
    if (!reportModalTxn || !currentRole) return;
    const url = reportUrlInput.trim();
    if (!url) { setReportError('Enter the report link.'); return; }
    setReportBusy(true);
    setReportError(null);
    setReportNotice(null);
    try {
      const response = await updateDiagnosticBookingReport(
        currentRole, reportModalTxn.transaction_id, url);
      if (response && response.metadata && response.metadata.api_status === true) {
        setReportUrlInput('');
        setReportNotice(response?.msg?.message || 'Report attached.');
        await refreshAfterReportChange();
        // Re-point the modal at the refreshed row so the new link shows in the list.
        setReportModalTxn((prev) => prev && { ...prev, report_url: response?.msg?.report_url || prev.report_url });
      } else {
        throw new Error(response?.msg?.error || 'Failed to attach report');
      }
    } catch (err) {
      setReportError(err.message || 'Failed to attach report');
    } finally {
      setReportBusy(false);
    }
  };

  const handleSendReport = async (url) => {
    if (!reportModalTxn || !currentRole) return;
    setReportBusy(true);
    setReportError(null);
    setReportNotice(null);
    try {
      const response = await sendDiagnosticBookingReport(
        currentRole, reportModalTxn.transaction_id, url);
      if (response && response.metadata && response.metadata.api_status === true) {
        setReportNotice(response?.msg?.message || 'Report link sent.');
      } else {
        // Surfaced, never swallowed: for this action the WhatsApp message IS the point,
        // so a failure has to reach Ops rather than look like success.
        throw new Error(response?.msg?.error || 'Failed to send report link');
      }
    } catch (err) {
      setReportError(err.message || 'Failed to send report link');
    } finally {
      setReportBusy(false);
    }
  };

  const handleStatusUpdateClick = (e, txn, targetStatus) => {
    e.stopPropagation(); // don't trigger the row's detail click
    if (!targetStatus) return;
    setSelectedTransactionForStatus({
      transactionId: txn.transaction_id,
      currentStatus: txn.status,
      nextStatus: targetStatus,
    });
    setStatusUpdateError(null);
    setShowStatusUpdateModal(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedTransactionForStatus || !currentRole) return;
    const { transactionId, nextStatus } = selectedTransactionForStatus;
    setUpdatingStatus(true);
    setStatusUpdateError(null);
    try {
      const response = await updateDiagnosticBookingStatus(currentRole, transactionId, nextStatus);
      if (response && response.metadata && response.metadata.api_status === true) {
        setShowStatusUpdateModal(false);
        setSelectedTransactionForStatus(null);
        // Refresh the snapshot so the new status is reflected
        isSnapshotLoadingRef.current = false;
        await loadDiagnosticSnapshot();
      } else {
        throw new Error(response?.msg?.error || 'Failed to update status');
      }
    } catch (err) {
      setStatusUpdateError(err.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
          <button
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/subscriber-wallet')}
              className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
            >
              ← Back to Home
            </button>
            <button
              onClick={() => navigate(backTarget.path)}
              className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
            >
              ← {backTarget.label}
            </button>
          </div>
        </div>

        {/* Page Heading */}
        <div className="mt-2 mb-2">
          <h1 className="text-3xl font-bold text-gray-900">Diagnostic Booking Profile</h1>
        </div>

        {/* Profile Section */}
        <div className="bg-white border border-gray-200 rounded-md p-4 sm:p-6 shadow-sm">
          <div className="space-y-6">
            {/* Header Section */}
            <div className="relative">
              <div>
                <div className="text-xl font-bold text-gray-900">{profile?.personal?.full_name || profile?.name || 'Subscriber'}</div>
                <div className="text-sm text-gray-600 flex items-center space-x-2 mt-1">
                  <span>Mobile No: {(isFinance || isSupport) ? (profile?.personal?.mobile || profile?.phoneFull) : (showPhone ? (profile?.personal?.mobile || profile?.phoneFull) : maskedPhone)}</span>
                  {!(isFinance || isSupport) && (
                    <button
                      onClick={() => setShowPhone(v => !v)}
                      className="text-gray-400 hover:text-gray-600"
                      aria-label={showPhone ? 'Hide phone' : 'Show phone'}
                    >
                      {showPhone ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">REF CODE</div>
                  <div className="text-sm font-medium text-gray-900">{profile?.subscription || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Validity Period</div>
                  <div className="text-sm font-medium text-gray-900">{formatDate(profile?.valid_period)}</div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Plan</div>
                  <div className="text-sm font-medium text-gray-900">{profile?.plan || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">KYC Status</div>
                  <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-md bg-green-500 text-white w-fit">{profile?.kyc || 'N/A'}</span>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Status</div>
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-md w-fit ${profile?.status === 'Active' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
                    }`}>
                    {profile?.status || 'N/A'}
                  </span>
                </div>
                <div></div>
              </div>
            </div>
          </div>
        </div>

        {/* Wallet Snapshot */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Wallet Snapshot</h2>
          {wallet?.wallet_snapshot?.ref_code && (
            <div className="text-sm text-gray-500">
              Ref: <span className="font-mono text-gray-700">{wallet.wallet_snapshot.ref_code}</span>
            </div>
          )}
        </div>

        {!wallet ? (
          <div className="bg-white border border-gray-200 rounded-md p-6 text-center">
            <div className="text-gray-500 mb-2">Loading wallet data...</div>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : wallet.error ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-6 text-center">
            <div className="text-red-600 mb-2">Failed to load wallet data</div>
            <div className="text-sm text-red-500 mb-3">{wallet.error}</div>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                label: 'WB (Wallet Balance)',
                value: currencyRounded(wallet?.wallet_snapshot?.balance || 0),
                icon: <Wallet className="w-4 h-4 text-blue-600" />
              },
              {
                label: 'EC (Eligible Cashback)',
                value: currencyRounded(wallet?.wallet_snapshot?.eligible_cashback || 0),
                icon: <img src={EligibleCashbackIcon} alt="Eligible Cashback" className="w-4 h-4" />
              },
              {
                label: 'RC (Redeemable Cashback)',
                value: currencyRounded(wallet?.wallet_snapshot?.redeemable_cashback || 0),
                icon: <img src={RedeemableCashbackIcon} alt="Redeemable Cashback" className="w-4 h-4" />
              },
              {
                label: 'LC (Lifetime Cashback)',
                value: currencyRounded(wallet?.wallet_snapshot?.lifetime_cashback || 0),
                icon: <img src={LifetimeCashbackIcon} alt="Lifetime Cashback" className="w-4 h-4" />
              },
            ].map((c) => (
              <div key={c.label} className="bg-white border border-gray-200 rounded-md p-4 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-blue-50 rounded-md flex items-center justify-center">
                    {c.icon}
                  </div>
                  <div className="text-sm text-gray-600">{c.label}</div>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-2">{c.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Diagnostic Booking Snapshot */}
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Diagnostic Booking Snapshot</h2>
          </div>

          {diagnosticSnapshotLoading ? (
            <div className="bg-white border border-gray-200 rounded-md p-6 text-center">
              <div className="text-gray-500 mb-2">Loading diagnostic booking snapshot...</div>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : diagnosticSnapshotError ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-6 text-center">
              <div className="text-red-600 mb-2">Failed to load diagnostic booking snapshot</div>
              <div className="text-sm text-red-500 mb-3">{diagnosticSnapshotError}</div>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          ) : diagnosticSnapshot?.items ? (
            <>
              {/* Transactions Table */}
              {diagnosticSnapshot.items && (
                <div className="bg-white border border-gray-200 rounded-md shadow-sm">
                  <div className="p-4 sm:p-6">
                    <div className="overflow-x-auto">
                      <table className="w-full border border-gray-200 rounded-md min-w-max">
                        <thead className="bg-gray-50">
                          <tr className="border-b border-gray-200">
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date & Time</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Service Provider</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Price</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Cashback</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">WB</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Family Members</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Payment Method</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Credit Balance</th>
                            {DIAGNOSTIC_STATUS_UPDATE_ENABLED && isOpsOrSuperAdmin && (
                              <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {diagnosticSnapshot.items && diagnosticSnapshot.items.length > 0 ? (
                            diagnosticSnapshot.items.map((txn) => (
                              <tr
                                key={txn.transaction_id}
                                onClick={() => handleRowClick(txn.transaction_id)}
                                className="border-b border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors"
                              >
                                <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">
                                  {(() => {
                                    const dateTimeValue = txn.date_time;
                                    // Ensure we never render an object directly
                                    return formatDateTime(dateTimeValue);
                                  })()}
                                </td>
                                <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">{txn.service_provider || 'N/A'}</td>
                                <td className="px-3 sm:px-6 py-2 font-semibold text-xs text-gray-900 whitespace-nowrap">
                                  ₹{(txn.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="px-3 sm:px-6 py-2 font-semibold text-xs text-gray-900 whitespace-nowrap">
                                  {currencyRounded(txn.cashback || 0)}
                                </td>
                                <td className="px-3 sm:px-6 py-2 font-semibold text-xs text-gray-900 whitespace-nowrap">
                                  {currencyRounded(txn.wallet_balance || 0)}
                                </td>
                                <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap" title={txn.family_member_names || ''}>
                                  {txn.family_member_names || 'N/A'}
                                </td>
                                <td className="px-3 sm:px-6 py-2 whitespace-nowrap">
                                  <span
                                    className="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border"
                                    style={getStatusBadgeStyle(txn.status)}
                                  >
                                    {txn.status || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">{(txn.status || '').toLowerCase() === 'initiated' ? 'N/A' : (txn.payment_method || 'N/A')}</td>
                                <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">
                                  {txn.credit_balance != null ? currencyRounded(txn.credit_balance) : '—'}
                                </td>
                                {DIAGNOSTIC_STATUS_UPDATE_ENABLED && isOpsOrSuperAdmin && (
                                  <td className="px-3 sm:px-6 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                    {(() => {
                                      // Manual status changes only for AyushPay-owned (non-partner) labs.
                                      const targets = isPartnerBooking(txn) ? [] : allowedDiagTargets(txn.status);
                                      if (targets.length === 0) {
                                        return <span className="text-xs text-gray-400">-</span>;
                                      }
                                      // Dropdown of valid next statuses (strictly sequential next + Refunded).
                                      // Selecting one opens the confirm modal for that target.
                                      return (
                                        <select
                                          value=""
                                          onChange={(e) => { if (e.target.value) handleStatusUpdateClick(e, txn, e.target.value); e.target.value = ''; }}
                                          className="px-2 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:border-green-500 focus:ring-2 focus:ring-green-500 focus:outline-none cursor-pointer"
                                        >
                                          <option value="" disabled>Change status…</option>
                                          {targets.map((t) => (
                                            <option key={t} value={t}>{humanizeStatus(t)}</option>
                                          ))}
                                        </select>
                                      );
                                    })()}
                                    {/* Reports are for AyushPay-owned labs only — a partner
                                        booking's reports arrive from the lab's own webhook,
                                        and the backend refuses an ops edit on those. */}
                                    {!isPartnerBooking(txn) && (
                                      <button
                                        type="button"
                                        onClick={(e) => openReportModal(e, txn)}
                                        className="ml-2 px-2 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                      >
                                        {txn.report_url ? 'Report ✓' : 'Report'}
                                      </button>
                                    )}
                                  </td>
                                )}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={DIAGNOSTIC_STATUS_UPDATE_ENABLED && isOpsOrSuperAdmin ? 10 : 9} className="px-6 py-8 text-center text-gray-500">
                                No transactions found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {diagnosticSnapshot.pagination && diagnosticSnapshot.pagination.total > 0 && (
                      <div className="mt-4 flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                          Showing {((diagnosticSnapshot.pagination.page - 1) * diagnosticSnapshot.pagination.items) + 1}-{Math.min(diagnosticSnapshot.pagination.page * diagnosticSnapshot.pagination.items, diagnosticSnapshot.pagination.total)} of {diagnosticSnapshot.pagination.total}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            disabled={diagnosticSnapshot.pagination.page === 1}
                            onClick={() => setSnapshotPage((p) => Math.max(1, p - 1))}
                            className={`px-3 py-1 border rounded-md text-sm ${diagnosticSnapshot.pagination.page === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                          >
                            Prev
                          </button>
                          {Array.from({ length: diagnosticSnapshot.pagination.total_pages || 1 }).map((_, i) => {
                            const pageNum = i + 1;
                            const totalPages = diagnosticSnapshot.pagination.total_pages || 1;
                            const shouldShow =
                              pageNum === 1 ||
                              pageNum === totalPages ||
                              (pageNum >= diagnosticSnapshot.pagination.page - 2 && pageNum <= diagnosticSnapshot.pagination.page + 2);

                            if (!shouldShow) {
                              if (pageNum === diagnosticSnapshot.pagination.page - 3 || pageNum === diagnosticSnapshot.pagination.page + 3) {
                                return (
                                  <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                                    ...
                                  </span>
                                );
                              }
                              return null;
                            }

                            return (
                              <button
                                key={pageNum}
                                onClick={() => setSnapshotPage(pageNum)}
                                className={`px-3 py-1 border rounded-md text-sm ${diagnosticSnapshot.pagination.page === pageNum ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          <button
                            disabled={diagnosticSnapshot.pagination.page >= (diagnosticSnapshot.pagination.total_pages || 1)}
                            onClick={() => setSnapshotPage((p) => Math.min(diagnosticSnapshot.pagination.total_pages || 1, p + 1))}
                            className={`px-3 py-1 border rounded-md text-sm ${diagnosticSnapshot.pagination.page >= (diagnosticSnapshot.pagination.total_pages || 1) ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Booking Detail Modal — shared with the subscriber portal, which renders the
            same component in place instead of navigating here. */}
        <DiagnosticBookingDetailModal
          mounted={mounted && !!selectedBookingId}
          bookingDetail={bookingDetail}
          loading={detailLoading}
          error={detailError}
          onRetry={() => handleRowClick(selectedBookingId)}
          onClose={closeModal}
          showFullMobile={!isSupport && mobilesUnmasked}
          partnerDataExpanded={partnerDataExpanded}
          onTogglePartnerData={handlePartnerDataExpand}
          currentRole={currentRole}
          canSendPaymentLink={isOpsOrSuperAdmin}
        />

        {/* Status Update Modal (AyushPay-owned labs) — rendered via portal, outside the row subtree */}
        {mounted && reportModalTxn && createPortal(
          <div
            className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50"
            onClick={closeReportModal}
          >
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Diagnostic Report</h3>
                <button onClick={closeReportModal} className="text-gray-400 hover:text-gray-600">
                  <FaTimes />
                </button>
              </div>

              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Report link</label>
                  <input
                    type="url"
                    value={reportUrlInput}
                    onChange={(e) => setReportUrlInput(e.target.value)}
                    placeholder="https://lab.example.com/report.pdf"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                  {/* https only: a report link is health data in transit, and the backend
                      rejects plain http rather than relying on the field type. */}
                  <p className="mt-1 text-xs text-gray-500">Must be an https link. Attaching replaces any link already on this booking; the previous one stays in the booking history.</p>
                </div>

                <button
                  type="button"
                  onClick={handleAttachReport}
                  disabled={reportBusy}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {reportBusy ? 'Working…' : 'Attach report'}
                </button>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Attached report</h4>
                  {!reportModalTxn.report_url ? (
                    <p className="text-xs text-gray-500">No report attached yet.</p>
                  ) : (
                    <div className="flex items-center justify-between gap-3 border border-gray-200 rounded-md px-3 py-2">
                      <a href={reportModalTxn.report_url} target="_blank" rel="noopener noreferrer"
                         className="text-xs text-blue-600 hover:underline truncate" title={reportModalTxn.report_url}>
                        {reportModalTxn.report_url}
                      </a>
                      {/* The URL is passed back explicitly rather than letting the server
                          pick: it confirms Ops is sending the link they are looking at,
                          and the backend refuses anything that is not the stored one. */}
                      <button
                        type="button"
                        onClick={() => handleSendReport(reportModalTxn.report_url)}
                        disabled={reportBusy}
                        className="shrink-0 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        Send on WhatsApp
                      </button>
                    </div>
                  )}
                </div>

                {reportNotice && (
                  <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                    {reportNotice}
                  </div>
                )}
                {reportError && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    {reportError}
                  </div>
                )}
              </div>

              <div className="flex justify-end px-6 py-4 border-t border-gray-200">
                <button onClick={closeReportModal}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {mounted && showStatusUpdateModal && selectedTransactionForStatus && createPortal(
          <div
            className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50"
            onClick={closeStatusUpdateModal}
          >
            <div
              className="bg-white rounded-lg shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Update Booking Status</h3>
                <button
                  onClick={closeStatusUpdateModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={updatingStatus}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-4 space-y-4">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Current Status</div>
                  <div className="text-base font-medium text-gray-900">{humanizeStatus(selectedTransactionForStatus.currentStatus) || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Update To</div>
                  <div className="text-base font-medium text-blue-600">{humanizeStatus(selectedTransactionForStatus.nextStatus)}</div>
                </div>
                {statusUpdateError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                    {statusUpdateError}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200">
                <button
                  onClick={closeStatusUpdateModal}
                  disabled={updatingStatus}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  disabled={updatingStatus}
                  className={`px-4 py-2 rounded-md font-medium transition-colors ${updatingStatus
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                  {updatingStatus ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default DiagnosticBookingProfile;

